<template lang="pug">
  div(
    v-if="status === 'select'"
    class="block-documents-download"
  )
    h2(
      class="text-left fw-700"
      :class="[xsDevice ? 'title-20px medium pt-3 pb-6' : 'title-32px pb-8']"
    ) {{ $t('Download') }}
    div(class="block-documents-download__actions")
      v-btn(
        :loading="buttonLoadings.original"
        class="block-documents-download__btn"
        @click="handleDownload('original')"
        data-test="download-original-btn"
      )
        div(class="block-documents-download__btn-content")
          span(class="text-14px medium dark") {{ $t('Original document') }}
          span(class="text-12px light") {{ $t('In its original form without changes or signatures') }}
        e-svg-icon(name="file-attach-2")
      v-btn(
        :loading="buttonLoadings.signed"
        class="block-documents-download__btn"
        @click="handleDownload('signed')"
        data-test="download-signed-btn"
      )
        div(class="block-documents-download__btn-content")
          span(class="text-14px medium dark") {{ $t('Signed document') }}
          span(class="text-12px light") {{ $t('With electronic signature in .p7s format') }}
        e-svg-icon(name="key-4")

      v-btn(
        :loading="buttonLoadings.protocol"
        class="block-documents-download__btn"
        @click="handleDownload('protocol')"
        data-test="download-protocol-btn"
      )
        div(class="block-documents-download__btn-content")
          span(class="text-14px medium dark") {{ $t('Printable version') }}
          span(class="text-12px light") {{ $t('Document with signing protocol') }}
        e-svg-icon(name="printer-3")

  div(
    v-else-if="status === 'progress'"
    class="block-documents-download"
  )
    h1(
      class="text-left fw-700"
      :class="[xsDevice ? 'title-20px medium pt-3 pb-6' : 'title-32px pb-8']"
    ) {{ $t('Download') }}
    div(class="block-documents-download__content")
      div(class="progress-row")
        span(class="text-14px medium dark") {{ $t('Downloaded documents') }}
        span(class="text-12px light") {{ $t('{processed} of {total} done', { processed, total }) }}

      v-progress-linear(
        rounded
        color="#8F257D"
        background-color="#121212"
        background-opacity="0.06"
        :value="progress"
        height="12"
      )
      div(class="progress-info")
        span(class="text-12px light") {{ $t('The overall process') }}
        span(class="text-12px light") {{ progress }}%
      v-btn(
        class="secondary-button error-btn"
        @click="cancelDownload"
      ) {{ $t('Cancel') }}

  div(
    v-else
    class="block-documents-download--done"
  )
    v-img(:src="require('~/assets/images/success3.png')")
    h1(
      class="text-left fw-700"
      :class="[xsDevice ? 'title-20px medium pt-3 pb-6' : 'title-32px pb-2 pt-4']"
    ) {{ $t('Download completed') }}
    p(class="text-14px light mb-8") {{ $t('{all} {total} {documents} are uploaded in "{format}" format', { format: typeDownloadText, total, documents: documentsWord, all: allWord }) }}
    v-btn(
      class="main-button"
      @click="closeModal"
    ) {{ $t('Ok') }}
</template>

<script>
import Download from 'js-file-download'
import { IntervalRequest } from '~/services/_utils/IntervalRequest'
import responsive from '~/mixins/pages/responsive'
import { getFilenameFromContentDisposition } from '~/services/_utils/utils'

export default {
  name: 'BlockDocumentsDownload',
  mixins: [responsive],
  props: {
    closeModal: {
      type: Function,
      default: () => {}
    },
    documentId: {
      type: String,
      default: null
    },
    documentIds: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      buttonLoadings: {
        original: false,
        signed: false,
        protocol: false
      },
      progress: 0,
      status: 'select',
      total: 0,
      processed: 0,
      downloadCanceled: false,
      typeDownload: null
    }
  },
  computed: {
    typeDownloadText() {
      const typesDownload = {
        original: this.$t('Original document'),
        signed: this.$t('Signed document'),
        protocol: this.$t('Version for print')
      }
      return typesDownload[this.typeDownload]
    },
    documentsWord() {
      const n = this.total
      if (n % 100 > 10 && n % 100 < 20) return this.$t('many documents')

      switch (n % 10) {
        case 1:
          return this.$t('one document')
        case 2:
        case 3:
        case 4:
          return this.$t('few documents')
        default:
          return this.$t('many documents')
      }
    },
    allWord() {
      return this.total > 1 ? this.$t('All') : ''
    }
  },
  methods: {
    async handleDownload(type) {
      this.status = 'progress'
      this.progress = 0
      this.total = this.documentIds?.length || 1
      this.typeDownload = type

      this.downloadCanceled = false

      const documentsIdsLength = this._.get(this.documentIds, 'length')
      if (this.documentId || documentsIdsLength === 1) {
        await this.documentDownload(type)
      } else if (this.documentIds) {
        await this.listDownload(type)
      }
    },
    async documentDownload(type) {
      try {
        this.buttonLoadings[type] = true
        const documentId = this.documentId || this._.get(this.documentIds, '[0]')
        const request = await this.$store.dispatch('documents/download', {
          id: documentId,
          params: {
            type: 'attachment',
            file: type
          }
        })
        const contentDisposition = this._.get(request, 'headers.content-disposition')
        const fileName = getFilenameFromContentDisposition(contentDisposition)
        const fullFileName = fileName.split('utf-8')[1]
        if (!this.downloadCanceled) {
          Download(this._.get(request, 'data'), fullFileName)
          this.status = 'done'
        }
      } catch (e) {
        const errorText = await e.response?.data.text()
        let error
        if (errorText) {
          error = JSON.parse(errorText)
        } else {
          error = e
        }
        this.$handlers.error(error, this)
        if (e.response?.status === 400) {
          this.closeModal(false)
        }
      } finally {
        this.buttonLoadings[type] = false
      }
    },
    async listDownload(type) {
      try {
        this.buttonLoadings[type] = true
        const payload = {
          type: 'export',
          documentIds: this.documentIds,
          exportType: type
        }
        const taskId = this._.get(
          await this.$store.dispatch('documents/downloadDocuments', payload),
          'data.id'
        )
        await this.pollStatus(taskId)
        const archiveResponse = await this.$store.dispatch('documents/downloadArchive', taskId)
        const arrayBuffer = this._.get(archiveResponse, 'data')
        if (!this.downloadCanceled) {
          Download(arrayBuffer, 'archive.zip')
          this.status = 'done'
        }
      } catch (e) {
        if (e.response?.status === 403) return
        this.$handlers.error(e, this)
      } finally {
        this.buttonLoadings[type] = false
      }
    },
    async pollStatus(taskId) {
      this.status = 'progress'

      const request = new IntervalRequest(
        () => this.$store.dispatch('documents/checkStatus', taskId),
        {
          interval: 1e3,
          maxDelay: 12e4 // ~2 minutes
        }
      )
      this.request = request

      await request.startExponential(res => {
        if (this.downloadCanceled) return true

        const { status, progress } = res.data
        this.progress = progress
        this.processed = Math.round((progress * this.total) / 100)
        return status === 'complete'
      })
    },
    cancelDownload() {
      if (this.status !== 'progress') return
      this.downloadCanceled = true
      this.request?.cancel?.()
      this.status = 'select'
      this.progress = 0
      this.typeDownload = null
    }
  }
}
</script>

<style lang="scss">
.block-documents-download {
  &--done {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;

    button {
      min-width: 100% !important;
    }
  }

  &__content {
    display: flex;
    justify-content: space-between;
    width: 100%;
    flex-direction: column;
    text-align: left;
    .secondary-button {
      min-width: 100% !important;
      margin-top: 64px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__btn {
    width: 100%;
    background: $background-gray !important;
    border-radius: 16px !important;
    min-height: 54px !important;

    display: flex;
    align-items: center;
    justify-content: space-between;

    &-content {
      display: flex;
      justify-content: space-between;
      width: 100%;
      flex-direction: column;
      text-align: left;
    }
    .v-btn__content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      white-space: normal;
      padding: 0 20px;
    }
  }
}
.progress {
  &-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
  }

  &-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 32px;
    margin-bottom: 12px;
  }
}
</style>
